import api from '../../utils/api';
import type {
  CampaignListResponse,
  PushCampaignExecution,
  PushNotificationCampaign,
  ScheduleType,
  TargetPlatform,
  ConditionFieldOption,
} from '../../models/MasterAdmin/pushNotificationCampaign';

const BASE = '/master-admin/notifications/campaigns';

export type CampaignStatsByTypeRow = {
  notificationType: string;
  totalDelivered: string | number;
  totalFailed: string | number;
  campaignCount: string | number;
};

export async function fetchCampaignStatsByType(): Promise<{
  success: boolean;
  data: { byType: CampaignStatsByTypeRow[] };
}> {
  const response = await api.get(`${BASE}/stats-by-type`);
  return response.data;
}

export async function fetchCampaigns(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  targetAudience?: string;
  notificationType?: string;
}): Promise<CampaignListResponse> {
  const { page = 1, limit = 20, search, status, targetAudience, notificationType } = params || {};
  const response = await api.get<CampaignListResponse>(BASE, {
    params: {
      page,
      limit,
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
      ...(targetAudience ? { targetAudience } : {}),
      ...(notificationType ? { notificationType } : {}),
    },
  });
  return response.data;
}

export async function getCampaign(id: string): Promise<{ success: boolean; data: PushNotificationCampaign }> {
  const response = await api.get(`${BASE}/${encodeURIComponent(id)}`);
  return response.data;
}

/** Comma-separated platforms, e.g. `android,ios` or `web`. Omit to use campaign saved targetPlatform. */
export async function estimateRecipients(
  id: string,
  params?: { platforms?: string }
): Promise<{
  success: boolean;
  data: {
    estimatedRecipients: number;
    baseAudienceCount?: number;
    afterConditionsCount?: number;
    reachableFcmCount?: number;
    countsAreExact?: boolean;
    conditionsApplied?: boolean;
    note?: string;
  };
}> {
  const response = await api.get(`${BASE}/${encodeURIComponent(id)}/estimate`, {
    params: params?.platforms ? { platforms: params.platforms } : undefined,
  });
  return response.data;
}

/** Live reach estimate on create/edit page (no campaign id required). */
export async function estimateRecipientsPreview(payload: {
  targetAudience: string;
  targetPlatform: string;
  platforms?: string;
  filterCity?: string;
  filterState?: string;
  targetUserId?: string;
  conditionTree?: string;
}): Promise<{
  success: boolean;
  data: {
    estimatedRecipients: number;
    baseAudienceCount?: number;
    afterConditionsCount?: number;
    reachableFcmCount?: number;
    countsAreExact?: boolean;
    conditionsApplied?: boolean;
    note?: string;
  };
}> {
  const response = await api.post(`${BASE}/estimate-preview`, payload);
  return response.data;
}

export async function fetchCampaignAnalysis(
  id: string,
  params?: { limit?: number; status?: string }
): Promise<{
  success: boolean;
  data: {
    campaign: PushNotificationCampaign & { templateName?: string | null };
    analysis: {
      attempted: number;
      sent: number;
      delivered: number;
      read: number;
      failed: number;
      queued: number;
      skipped: number;
      deliveryRatePct: number;
      readRatePct: number;
      failRatePct: number;
    };
    participants: import('../../models/MasterAdmin/pushNotificationHub').PushDeliveryLog[];
    participantTotal: number;
  };
}> {
  const response = await api.get(`${BASE}/${encodeURIComponent(id)}/analysis`, { params });
  return response.data;
}

function appendFields(fd: FormData, values: Record<string, string | undefined>) {
  Object.entries(values).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v);
  });
}

export type CampaignFormPayload = {
  title: string;
  message: string;
  notificationType: string;
  intentType: string;
  intentValue?: string;
  targetAudience: string;
  targetPlatform: TargetPlatform;
  filterCity?: string;
  filterState?: string;
  scheduleMode: 'now' | 'later' | 'recurring';
  scheduleType?: ScheduleType | string;
  scheduledAt?: string;
  scheduleTimezone?: string;
  scheduleConfig?: string;
  conditionTree?: string;
  saveAsDraft: boolean;
  image?: File | null;
  targetUserId?: string;
  templateId?: string;
  behaviourSegmentKey?: string;
  imageUrl?: string;
  bannerImageUrl?: string;
  clearImage?: boolean;
};

function appendCampaignFields(fd: FormData, form: CampaignFormPayload) {
  appendFields(fd, {
    title: form.title,
    message: form.message,
    notificationType: form.notificationType,
    intentType: form.intentType,
    intentValue: form.intentValue || '',
    targetAudience: form.targetAudience,
    targetPlatform: form.targetPlatform,
    filterCity: form.filterCity || '',
    filterState: form.filterState || '',
    scheduleMode: form.scheduleMode,
    scheduleType: form.scheduleType || '',
    scheduledAt: form.scheduledAt || '',
    scheduleTimezone: form.scheduleTimezone || 'Asia/Kolkata',
    scheduleConfig: form.scheduleConfig || '',
    conditionTree: form.conditionTree || '',
    saveAsDraft: String(form.saveAsDraft),
    targetUserId: form.targetUserId || '',
    templateId: form.templateId || '',
    behaviourSegmentKey: form.behaviourSegmentKey || '',
    imageUrl: form.imageUrl || '',
    bannerImageUrl: form.bannerImageUrl || '',
  });
}

export async function createCampaign(
  form: CampaignFormPayload
): Promise<{ success: boolean; data: PushNotificationCampaign }> {
  const fd = new FormData();
  appendCampaignFields(fd, form);
  if (form.image) fd.append('image', form.image);
  const response = await api.post(BASE, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function updateCampaign(
  id: string,
  form: CampaignFormPayload
): Promise<{ success: boolean; data: PushNotificationCampaign }> {
  const fd = new FormData();
  appendCampaignFields(fd, form);
  if (form.clearImage) fd.append('clearImage', 'true');
  if (form.image) fd.append('image', form.image);
  const response = await api.put(`${BASE}/${encodeURIComponent(id)}`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function deleteCampaign(id: string): Promise<void> {
  await api.delete(`${BASE}/${encodeURIComponent(id)}`);
}

export async function sendCampaign(
  id: string,
  body: { platforms: ('android' | 'ios' | 'web' | 'both')[] }
): Promise<{
  success: boolean;
  data: { campaign: PushNotificationCampaign; estimatedRecipients: number; message?: string };
}> {
  const response = await api.post(`${BASE}/${encodeURIComponent(id)}/send`, body);
  return response.data;
}

export async function fetchConditionOptions(): Promise<{
  success: boolean;
  data: { fields: ConditionFieldOption[]; combinators: string[]; maxDepth: number; maxRules: number };
}> {
  const response = await api.get(`${BASE}/condition-options`);
  return response.data;
}

export async function fetchCampaignExecutions(
  id: string,
  params?: { page?: number; limit?: number }
): Promise<{
  success: boolean;
  data: { items: PushCampaignExecution[]; pagination: { currentPage: number; totalPages: number; totalItems: number } };
}> {
  const response = await api.get(`${BASE}/${encodeURIComponent(id)}/executions`, { params });
  return response.data;
}

export async function pauseCampaign(id: string): Promise<{ success: boolean; data: PushNotificationCampaign }> {
  const response = await api.post(`${BASE}/${encodeURIComponent(id)}/pause`);
  return response.data;
}

export async function resumeCampaign(id: string): Promise<{ success: boolean; data: PushNotificationCampaign }> {
  const response = await api.post(`${BASE}/${encodeURIComponent(id)}/resume`);
  return response.data;
}

export async function cancelCampaign(id: string): Promise<{ success: boolean; data: PushNotificationCampaign }> {
  const response = await api.post(`${BASE}/${encodeURIComponent(id)}/cancel`);
  return response.data;
}
